import React, { useCallback, useEffect, useState } from "react";
import { Loader2, FileText, CheckCircle2, XCircle, Inbox, ExternalLink, MoreVertical, Eye, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { api, unwrap, API_BASE, getToken } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { BackgroundCheckSection, bgCheckBadge } from "@/pages/admin/AdminBackgroundCheckSection";

const STATUS_BADGE = {
  pending_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  conditional_offer_sent: "bg-amber-50 text-amber-700 border-amber-200",
  background_check_pending: "bg-sky-50 text-sky-700 border-sky-200",
  background_check_in_progress: "bg-cyan-50 text-cyan-700 border-cyan-200",
  background_check_completed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  adverse_action_dispute_window: "bg-rose-50 text-rose-700 border-rose-200",
  rejected_after_bg_check: "bg-rose-50 text-rose-700 border-rose-200",
};

const BG_CHECK_FILTER_OPTIONS = [
  { value: "", label: "All background check statuses" },
  { value: "not_started", label: "— (not started)" },
  { value: "pending_consent", label: "Pending consent" },
  { value: "ready_to_check", label: "Ready to check" },
  { value: "in_progress", label: "In progress" },
  { value: "clear", label: "Clear" },
  { value: "consider", label: "Consider" },
  { value: "fail", label: "Fail" },
];

const HOURS_LABEL = {
  mornings: "Mornings (7 AM – 12 PM)", midday: "Midday (11 AM – 3 PM)",
  afternoons: "Afternoons (12 PM – 5 PM)", evenings: "Evenings (5 PM – 9 PM)",
  full_day: "Full day (7 AM – 5 PM)", flexible: "Flexible / Any time",
};
const COMMITMENT_LABEL = {
  full_time: "Full-time (40+ hrs/week)", part_time: "Part-time (20–39 hrs/week)",
  flexible: "Flexible / As needed (< 20 hrs/week)",
};
const TRANSPORT_LABEL = {
  personal_vehicle: "Personal vehicle", family_friend: "Ride from family/friend",
  public: "Public transportation", other: "Other",
};

function Avatar({ src, name, size = 40 }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (src) return <img src={src} alt={name} width={size} height={size} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  return <div style={{ width: size, height: size, fontSize: size / 2.8 }} className="rounded-full bg-[#0EA5A4]/15 text-[#0F766E] grid place-items-center font-bold shrink-0">{initials}</div>;
}

export default function AdminApplications() {
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [bgCheckFilter, setBgCheckFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (bgCheckFilter) params.set("bg_check_status", bgCheckFilter);
      if (showArchived) params.set("include_archived", "true");
      const qs = params.toString();
      const r = await api.get(`/admin/cleaner-applications${qs ? `?${qs}` : ""}`);
      setItems(unwrap(r)?.items || []);
    } finally { setLoading(false); }
  }, [statusFilter, bgCheckFilter, showArchived]);

  useEffect(() => { load(); }, [load]);

  const archive = async (a, archived) => {
    setBusyId(a.id);
    try {
      await api.patch(`/admin/cleaner-applications/${a.id}/archive`, { archived });
      toast.success(archived ? "Application archived" : "Application restored");
      load();
    } catch { toast.error("Action failed"); }
    finally { setBusyId(null); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await api.delete(`/admin/cleaner-applications/${deleteTarget.id}`);
      toast.success("Application permanently deleted");
      setDeleteTarget(null);
      load();
    } catch { toast.error("Delete failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-applications">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Applications</h1>
          <p className="text-[#4B5563] text-sm mt-1">{items.length} cleaner application(s)</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer" data-testid="show-archived-toggle">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-switch" />
            Show archived
          </label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="applications-status-filter">
            <option value="">All statuses</option>
            <option value="pending_review">Pending review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={bgCheckFilter} onChange={(e) => setBgCheckFilter(e.target.value)} className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="applications-bg-check-filter">
            {BG_CHECK_FILTER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-[#E5E7EB] text-center py-16 text-[#4B5563]">
          <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" /> No applications yet.
        </Card>
      ) : (
        <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F4] text-[#4B5563]">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Applicant</th>
                  <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Location</th>
                  <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Experience</th>
                  <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Submitted</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-left font-semibold px-4 py-3">BG Check</th>
                  <th className="text-right font-semibold px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className={`border-t border-[#E5E7EB] hover:bg-[#FAFAF9] ${a.is_archived ? "opacity-60" : ""}`} data-testid={`application-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={a.full_name} />
                        <div className="min-w-0">
                          <div className="font-semibold truncate flex items-center gap-2">
                            {a.full_name}
                            {a.is_archived && <Badge className="border bg-stone-100 text-stone-500 border-stone-200 font-medium shrink-0" data-testid={`archived-badge-${a.id}`}>Archived</Badge>}
                          </div>
                          <div className="text-xs text-[#9CA3AF] truncate">{a.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-[#4B5563]">{[a.city, a.state, a.country].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[#4B5563]">{a.years_experience || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[#9CA3AF] text-xs">{formatDateTime(a.submitted_at, locale)}</td>
                    <td className="px-4 py-3"><Badge className={`border font-medium ${STATUS_BADGE[a.status] || ""}`}>{a.status.replace(/_/g, " ")}</Badge></td>
                    <td className="px-4 py-3">
                      <Badge className={`border font-medium ${bgCheckBadge(a.bg_check_status).cls}`} data-testid={`bg-check-badge-${a.id}`}>
                        {bgCheckBadge(a.bg_check_status).label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="rounded-xl h-8 w-8" disabled={busyId === a.id} data-testid={`application-actions-${a.id}`}>
                            {busyId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => setSelectedId(a.id)} data-testid={`view-application-${a.id}`}>
                            <Eye className="w-4 h-4 mr-2" /> View
                          </DropdownMenuItem>
                          {a.is_archived ? (
                            <DropdownMenuItem onClick={() => archive(a, false)} data-testid={`restore-application-${a.id}`}>
                              <ArchiveRestore className="w-4 h-4 mr-2" /> Restore
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => archive(a, true)} data-testid={`archive-application-${a.id}`}>
                              <Archive className="w-4 h-4 mr-2" /> Archive
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setDeleteTarget(a)} className="text-rose-600 focus:text-rose-700 focus:bg-rose-50" data-testid={`delete-application-${a.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {selectedId && (
        <ApplicationDetail appId={selectedId} onClose={() => setSelectedId(null)} onChanged={() => { setSelectedId(null); load(); }} onSilentRefresh={load} locale={locale} />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-application-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this application permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>{deleteTarget?.full_name}</b>&apos;s application from the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-confirm-btn">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ApplicationDetail({ appId, onClose, onChanged, onSilentRefresh, locale }) {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [idUrl, setIdUrl] = useState(null);
  const [idLoading, setIdLoading] = useState(false);

  const fetchApp = useCallback(async () => {
    try {
      const r = await api.get(`/admin/cleaner-applications/${appId}`);
      const d = unwrap(r);
      setApp(d);
      setNotes(d?.reviewer_notes || "");
    } catch { toast.error("Could not load application"); }
  }, [appId]);

  useEffect(() => {
    let active = true;
    (async () => { await fetchApp(); if (active) setLoading(false); })();
    return () => { active = false; };
  }, [fetchApp]);

  const refreshAfterBgCheckAction = async () => { await fetchApp(); onSilentRefresh?.(); };

  // Cleanup blob url
  useEffect(() => () => { if (idUrl) URL.revokeObjectURL(idUrl); }, [idUrl]);

  const viewId = async () => {
    setIdLoading(true);
    try {
      const r = await fetch(`${API_BASE}/admin/cleaner-applications/${appId}/government-id`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error("fetch failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setIdUrl(url);
      window.open(url, "_blank", "noopener");
    } catch { toast.error("Could not load the document"); }
    finally { setIdLoading(false); }
  };

  const approve = async () => {
    if (!window.confirm(`Approve ${app.full_name}? This creates a cleaner profile and emails login credentials.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/cleaner-applications/${appId}/approve`);
      toast.success("Application approved — cleaner created & credentials emailed");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not approve"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!window.confirm(`Reject ${app.full_name}? They'll receive a polite decline email.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/cleaner-applications/${appId}/reject`, { reviewer_notes: notes });
      toast.success("Application rejected");
      onChanged();
    } catch { toast.error("Could not reject"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter">Review application</DialogTitle>
          <DialogDescription>Verify the applicant's details, photo and ID before approving.</DialogDescription>
        </DialogHeader>

        {loading || !app ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <Avatar src={app.photo_url} name={app.full_name} size={88} />
              <div className="min-w-0">
                <div className="font-display text-xl font-bold">{app.full_name}</div>
                <Badge className={`border font-medium mt-1 ${STATUS_BADGE[app.status] || ""}`}>{app.status.replace(/_/g, " ")}</Badge>
                {app.bio && <p className="text-sm text-[#4B5563] mt-2">{app.bio}</p>}
              </div>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Row k="Email" v={app.email} />
              <Row k="Phone" v={app.phone} />
              <Row k="Date of birth" v={app.date_of_birth || "—"} />
              <Row k="Location" v={[app.city, app.state, app.country].filter(Boolean).join(", ") || "—"} />
              <Row k="Experience" v={app.years_experience || "—"} />
              <Row k="Languages" v={(app.languages || []).map((l) => l.toUpperCase()).join(", ") || "—"} />
              <Row k="Specialties" v={(app.specialties || []).join(", ") || "—"} />
              <Row k="Submitted" v={formatDateTime(app.submitted_at, locale)} />
            </dl>

            {app.references && (
              <div><div className="text-xs text-[#4B5563] mb-1">References</div><div className="text-sm whitespace-pre-wrap p-3 rounded-xl bg-[#F5F5F4]">{app.references}</div></div>
            )}

            <div className="rounded-xl border border-[#E5E7EB] p-4 space-y-2">
              <div className="text-sm font-semibold text-[#4B5563]">Availability & Transportation</div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="application-availability-section">
                <Row k="Available days" v={(app.available_days || []).map((d) => d[0].toUpperCase() + d.slice(1, 3)).join(" · ") || "—"} />
                <Row k="Preferred hours" v={HOURS_LABEL[app.preferred_hours] || "—"} />
                <Row k="Weekly commitment" v={COMMITMENT_LABEL[app.weekly_commitment] || "—"} />
                <Row k="Reliable transport" v={app.has_reliable_transportation ? "Yes" : "No"} />
                {app.has_reliable_transportation && (
                  <Row k="Transportation type" v={app.transportation_type === "other" ? (app.transportation_other || "Other") : (TRANSPORT_LABEL[app.transportation_type] || "—")} />
                )}
              </dl>
              {app.availability_commitment_accepted ? (
                <div className="text-sm text-emerald-700 flex items-center gap-1.5 pt-1" data-testid="commitment-confirmed">
                  <CheckCircle2 className="w-4 h-4" /> Confirmed on {formatDateTime(app.availability_commitment_accepted_at, locale)}
                </div>
              ) : (
                <div className="text-sm text-rose-600 font-medium pt-1" data-testid="commitment-not-confirmed">
                  ⚠ Applicant did not confirm commitment — review before approving
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[#E5E7EB] p-4 space-y-3">
              <div className="text-sm font-semibold flex items-center gap-2 text-[#4B5563]">
                <FileText className="w-4 h-4 text-[#0EA5A4]" /> Identity verification
              </div>
              <div className="grid grid-cols-3 gap-3" data-testid="identity-triplet">
                <PhotoTile label="Profile photo" testid="identity-profile-photo" src={app.photo_url} />
                <AuthPhotoTile
                  label="Government ID"
                  testid="identity-government-id"
                  endpoint={`/admin/cleaner-applications/${appId}/government-id`}
                  isPdf={(app.government_id_content_type || "").includes("pdf")}
                />
                <AuthPhotoTile
                  label="Live selfie"
                  testid="identity-live-selfie"
                  endpoint={`/admin/cleaner-applications/${appId}/selfie-live`}
                />
              </div>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={viewId} disabled={idLoading} data-testid="view-government-id-btn">
                {idLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-2" /> Open ID in new tab</>}
              </Button>
            </div>

            <BackgroundCheckSection appId={appId} appStatus={app.status} onChanged={refreshAfterBgCheckAction} locale={locale} />

            {app.status === "pending_review" ? (
              <>
                <div>
                  <label className="text-xs text-[#4B5563]">Reviewer notes (optional — included in rejection email)</label>
                  <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-xl mt-1" data-testid="reviewer-notes" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button onClick={reject} disabled={busy} variant="outline" className="rounded-xl text-rose-600 border-rose-300 hover:bg-rose-50 font-semibold" data-testid="reject-application-btn">
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                  <Button onClick={approve} disabled={busy} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="approve-application-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Approve</>}
                  </Button>
                </div>
              </>
            ) : (
              <div className="rounded-xl bg-[#F5F5F4] p-3 text-sm text-[#4B5563]">
                Reviewed by <b>{app.reviewed_by || "—"}</b>{app.reviewer_notes ? <> · {app.reviewer_notes}</> : null}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-[#F5F5F4] py-1">
      <dt className="text-[#4B5563]">{k}</dt>
      <dd className="font-medium text-right break-all">{v}</dd>
    </div>
  );
}

function PhotoTile({ label, src, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{label}</div>
      <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F4] border border-[#E5E7EB]">
        {src ? (
          <img src={src} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-xs text-[#9CA3AF]">—</div>
        )}
      </div>
    </div>
  );
}

function AuthPhotoTile({ label, endpoint, testid, isPdf = false }) {
  // Identity assets (gov ID + live selfie) sit behind admin auth; we have to
  // fetch them as blobs with the bearer token before rendering.
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    if (isPdf) return;
    let active = true;
    let blobUrl = "";
    (async () => {
      try {
        const r = await fetch(`${API_BASE}${endpoint}`, { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!r.ok) throw new Error("fetch failed");
        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        if (active) setUrl(blobUrl);
      } catch { if (active) setError(true); }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [endpoint, isPdf]);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{label}</div>
      <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F4] border border-[#E5E7EB] grid place-items-center">
        {isPdf ? (
          <div className="text-xs text-[#4B5563] font-medium">PDF</div>
        ) : error ? (
          <div className="text-xs text-rose-500">Not found</div>
        ) : url ? (
          <img src={url} alt={label} className="w-full h-full object-cover" />
        ) : (
          <Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" />
        )}
      </div>
    </div>
  );
}
